import {
  FormControl,
  FormLabel,
  MenuItem,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  TooltipProps,
} from '@material-ui/core';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@material-ui/icons';
import cx from 'classnames';
import { ReactNode, useMemo } from 'react';

import { t } from '@core/i18n';
import { ClassesProp } from '@core/theme/utils/with-styles';
import { Ellipsis } from '@shared/components/ellipsis';
import { FieldHelperText } from '@shared/components/field-helper-text';
import { Tooltip } from '@shared/components/tooltip';
import { ButtonSize, NewButtonColour, SelectColour } from '@shared/types/common/button';
import { generateRandomKey } from '@shared/utils/common';

import { useSelectClasses } from './Select.styles';

// ****************************************
// * !! DO NOT USE COMPONENT DIRECTLY  !! *
// * !! USE SingleSelect / MultiSelect !! *
// ****************************************

export interface Option<T extends Id = Id> {
  label: string;
  icon?: ReactNode;
  id: T;
  disabled?: boolean;
  show?: boolean;
  tooltip?: string;
}

export type OptionHash<TValue extends Id = Id> = Record<TValue, Option<TValue>>;

export type SingleSelectOnChange<TValue extends Id = Id> = (value: TValue) => void;

export type MultiSelectOnChange<TValue extends Id = Id> = (value: TValue[]) => void;

export type SelectOnChange<TValue extends Id = Id> = (value: TValue | TValue[]) => void;

// ****************************************
// * !! DO NOT USE COMPONENT DIRECTLY  !! *
// * !! USE SingleSelect / MultiSelect !! *
// ****************************************

export const generateOptionsHash = <TValue extends Id = Id>(options: Option<TValue>[]): OptionHash<TValue> =>
  options.reduce((result, option) => {
    if (typeof option.id === 'number') {
      result[option.id] = option;
    } else if (typeof option.id === 'string' && option.id) {
      result[option.id] = option;
    }

    return result;
  }, {} as OptionHash<TValue>);

const getSelectedValueString = <TValue extends Id = Id>(
  optionsHash: OptionHash<TValue>,
  multiple?: boolean,
  data?: TValue | TValue[]
): string => {
  const value = getValueOrDefault(multiple, data);

  if (Array.isArray(value)) {
    // Multi select
    return value.map((id) => optionsHash[id]?.label).join(', ');
  } else {
    // Single select
    return optionsHash[value]?.label || '';
  }
};

const getValueOrDefault = <TValue extends Id = Id>(
  multiple?: boolean,
  value?: TValue | TValue[]
): TValue | TValue[] => {
  if (isValueLengthy(multiple, value)) {
    return multiple ? (value as TValue[]) : (value as TValue);
  }

  return multiple ? [] : ('' as TValue);
};

const isValueLengthy = <TData extends Id | Id[]>(multiple?: boolean, data?: TData): boolean => {
  if (Array.isArray(data)) {
    return !!multiple && data.length > 0;
  } else if (typeof data === 'string') {
    return !multiple && data !== '';
  } else if (typeof data === 'number') {
    return !multiple;
  }

  return false;
};

// ****************************************
// * !! DO NOT USE COMPONENT DIRECTLY  !! *
// * !! USE SingleSelect / MultiSelect !! *
// ****************************************

export interface SelectProps<TValue extends Id = Id>
  extends Pick<MuiSelectProps, 'className' | 'endAdornment' | 'required' | 'startAdornment' | 'onOpen' | 'onClose'> {
  action?: ReactNode;
  active?: boolean;
  classes?: ClassesProp<typeof useSelectClasses>;
  colour?: SelectColour;
  children?: ReactNode | undefined;
  customValue?: string;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  fullWidth?: boolean;
  hovered?: boolean;
  icon?: ReactNode;
  infoText?: string;
  label?: ReactNode;
  multiple?: boolean;
  options: Option<TValue>[];
  optionsHash: OptionHash<TValue>;
  placeholder?: string;
  size?: ButtonSize;
  tooltip?: string;
  tooltipPlacement?: TooltipProps['placement'];
  value?: TValue | TValue[];
  onChange?: SelectOnChange<TValue>;
}

// ****************************************
// * !! DO NOT USE COMPONENT DIRECTLY  !! *
// * !! USE SingleSelect / MultiSelect !! *
// ****************************************

export const Select = <TValue extends Id = Id>({
  action,
  active = false,
  classes: propClasses,
  className,
  colour = NewButtonColour.Outline,
  customValue,
  disabled = false,
  endAdornment,
  error = false,
  errorText,
  fullWidth = false,
  hovered = false,
  icon,
  infoText = '',
  label,
  multiple,
  options,
  optionsHash,
  placeholder = t('select'),
  required,
  size = ButtonSize.small,
  startAdornment,
  tooltip,
  tooltipPlacement = 'left',
  value,
  onChange,
  onClose,
  onOpen,
}: SelectProps<TValue>) => {
  const classes = useSelectClasses(propClasses);

  const initialValue: TValue | TValue[] = useMemo(() => getValueOrDefault(multiple, value), []);

  const hasError = useMemo(() => error && !!errorText, [error, errorText]);

  const handleClose = (e: React.ChangeEvent<any>) => {
    if (onClose) {
      onClose(e);
    }

    setTimeout(() => {
      // @ts-ignore
      document?.activeElement?.blur();
    }, 0);
  };

  const placeholderVisible = useMemo(() => !isValueLengthy(multiple, value), [multiple, value]);

  const renderSelectorValue = (data: TValue | TValue[]) => {
    let valueContent = <></>;
    if (placeholderVisible) {
      valueContent = (
        <Ellipsis withTooltip={false} maxWidth="100%" text={placeholder} classes={{ root: classes.valuePlaceholder }} />
      );
    } else {
      valueContent = (
        <span
          className={classes.valueText}
          // TODO: customValue needs refactoring / review
          dangerouslySetInnerHTML={{ __html: customValue || getSelectedValueString(optionsHash, multiple, data) }}
        />
      );
    }

    return (
      <div
        className={cx(classes.value, classes[size], {
          [classes.valueDisabled]: disabled,
          [classes.valueMultiple]: multiple,
        })}
      >
        {!!icon && <span className={classes.valueIcon}>{icon}</span>}
        {valueContent}
      </div>
    );
  };

  const renderOption = (option: Option) => {
    const key = generateRandomKey();

    const optionContent = (
      <MenuItem
        classes={{
          root: cx(classes.menuItem, { [classes.menuItemDisabled]: option.disabled }),
          selected: classes.menuItemActive,
        }}
        key={key}
        value={option.id}
      >
        {!!option?.icon && <span className={classes.menuItemIcon}>{option?.icon}</span>}
        <span className={classes.menuItemText} dangerouslySetInnerHTML={{ __html: option.label }} />
      </MenuItem>
    );

    if (option.disabled) {
      return (
        <Tooltip data-value={option.id} placement="left" key={key} title={option.tooltip as string}>
          {optionContent}
        </Tooltip>
      );
    }

    return optionContent;
  };
  let content = (
    <>
      {label && (
        <FormLabel
          classes={{ root: classes.labelRoot, error: classes.labelError }}
          component="legend"
          error={error}
          required={required}
        >
          {label}
          {action}
        </FormLabel>
      )}
      <MuiSelect
        classes={{ root: cx(classes.select, classes[size]) }}
        className={className}
        defaultValue={initialValue}
        disabled={disabled}
        displayEmpty
        endAdornment={endAdornment}
        error={error}
        IconComponent={KeyboardArrowDownIcon}
        required={required}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
          classes: { paper: classes.menu },
        }}
        multiple={multiple}
        startAdornment={startAdornment}
        value={value ?? initialValue}
        onChange={(e) => {
          if (onChange) {
            const value = getValueOrDefault(multiple, e.target.value as undefined | TValue | TValue[]);
            onChange(value);
          }
        }}
        onClose={handleClose}
        onOpen={onOpen}
        renderValue={(data) => renderSelectorValue(data as TValue | TValue[])}
      >
        {options
          .filter((option) => typeof option.show === 'undefined' || option.show)
          .map((option) => renderOption(option))}
      </MuiSelect>
      <FieldHelperText
        classes={{ root: classes.helperText }}
        error={hasError}
        errorText={errorText}
        infoText={infoText}
      />
    </>
  );
  if (tooltip) {
    content = (
      <Tooltip placement={tooltipPlacement} title={tooltip}>
        {content}
      </Tooltip>
    );
  }

  return (
    <FormControl
      variant="outlined"
      classes={{
        root: cx(classes.root, classes[colour], classes[size], {
          [classes.selectActive]: active,
          [classes.selectHovered]: hovered,
          [classes.selectDisabled]: disabled,
          [classes.rootFullWidth]: fullWidth,
        }),
      }}
      required={required}
      error={error}
    >
      {content}
    </FormControl>
  );
};
